import type { PriceBookColumn, PriceBookColumnKey } from '../types/BlockContent/PriceBookContent';
import { defaultContentForKind } from './defaultContentForKind';

/**
 * The 12-key contract (LLAMS-610 Part 4 widened `PriceBookColumnKey` from 8 to 12: `job_type`,
 * `flat_price`, `mobilization`, `travel` are new). `normalizePriceBookColumnKey` recognizes all 12
 * so a block carrying one of the new keys is not silently collapsed to `'name'` — but
 * `mergePriceBookColumnsWithDefaults` below still upgrades to the unchanged 8-column default
 * shape (see its own docstring), so the new keys only round-trip through the raw
 * `PriceBookColumn[]`, not through that merge.
 */
const CANONICAL_KEYS: ReadonlySet<string> = new Set([
  'name',
  'description',
  'job_type',
  'flat_price',
  'unit',
  'rate',
  'ot_rate',
  'ot_rate_2',
  'volume_rate',
  'mobilization',
  'travel',
  'min_charge'
]);

/** Maps legacy camelCase and wire snake_case column keys to canonical POC keys. */
const KEY_ALIASES: Readonly<Record<string, PriceBookColumnKey>> = {
  minCharge: 'min_charge',
  min_charge: 'min_charge',
  otRate: 'ot_rate',
  ot_rate: 'ot_rate',
  otRate2: 'ot_rate_2',
  ot_rate_2: 'ot_rate_2',
  volumeRate: 'volume_rate',
  volume_rate: 'volume_rate',
  name: 'name',
  description: 'description',
  unit: 'unit',
  rate: 'rate',
  jobType: 'job_type',
  job_type: 'job_type',
  flatPrice: 'flat_price',
  flat_price: 'flat_price',
  mobilization: 'mobilization',
  travel: 'travel'
};

export function normalizePriceBookColumnKey(raw: string): PriceBookColumnKey {
  const trimmed = raw.trim();
  const mapped = KEY_ALIASES[trimmed];
  if (mapped) {
    return mapped;
  }

  if (CANONICAL_KEYS.has(trimmed)) {
    return trimmed as PriceBookColumnKey;
  }

  return 'name';
}

export function isPriceBookNumericColumn(key: string): boolean {
  return (
    key === 'rate' ||
    key === 'min_charge' ||
    key === 'ot_rate' ||
    key === 'ot_rate_2' ||
    key === 'volume_rate' ||
    key === 'minCharge' ||
    key === 'otRate' ||
    key === 'otRate2' ||
    key === 'volumeRate' ||
    key === 'flat_price' ||
    key === 'flatPrice' ||
    key === 'mobilization' ||
    key === 'travel'
  );
}

function pocDefaultColumns(): PriceBookColumn[] {
  const defaults = defaultContentForKind('price_book');
  return defaults.kind === 'price_book' ? defaults.columns.map((col) => ({ ...col })) : [];
}

/**
 * Ensures every price_book block exposes the full 8-column POC layout.
 * Legacy 5-column library blocks are upgraded while preserving saved labels and visibility.
 *
 * DELIBERATELY UNCHANGED by LLAMS-610 Part 4's key widening: this still upgrades to the same
 * 8-column `defaultContentForKind('price_book')` shape it always did, so existing content, both
 * renderers and generated PDFs stay byte-identical. Because it iterates the (still 8-key)
 * `defaults` list and only carries over an input column when a default entry shares its key, any
 * of the four new keys (`job_type`, `flat_price`, `mobilization`, `travel`) present in `columns` is
 * appended after the default columns, preserving each key's label and visibility. A legacy
 * 8-column input still returns the same eight columns.
 */
export function mergePriceBookColumnsWithDefaults(columns: PriceBookColumn[]): PriceBookColumn[] {
  const defaults = pocDefaultColumns();
  if (defaults.length === 0) {
    return columns;
  }

  const normalized = columns.map((col) => ({
    ...col,
    key: normalizePriceBookColumnKey(String(col.key))
  }));

  const byKey = new Map(normalized.map((col) => [col.key, col]));
  return defaults
    .map((def) => {
      const existing = byKey.get(def.key);
      if (!existing) {
        return { ...def };
      }

      const label = def.key === 'rate' && existing.label.trim().toLowerCase() === 'rate' ? def.label : existing.label || def.label;

      return {
        key: def.key,
        label,
        visible: existing.visible,
        order: def.order
      };
    })
    .sort((a, b) => a.order - b.order)
    .concat(appendPreservedPriceBookColumns(normalized, defaults));
}

const PRESERVED_PRICE_BOOK_KEYS = ['job_type', 'flat_price', 'mobilization', 'travel'] as const;

function appendPreservedPriceBookColumns(normalized: PriceBookColumn[], defaults: PriceBookColumn[]): PriceBookColumn[] {
  const defaultKeys = new Set(defaults.map((column) => column.key));
  const maxOrder = defaults.reduce((max, column) => Math.max(max, column.order), -1);
  let nextOrder = maxOrder + 1;
  return normalized
    .filter(
      (column) =>
        PRESERVED_PRICE_BOOK_KEYS.includes(column.key as (typeof PRESERVED_PRICE_BOOK_KEYS)[number]) && !defaultKeys.has(column.key)
    )
    .map((column) => {
      const preserved = { ...column, order: nextOrder };
      nextOrder += 1;
      return preserved;
    });
}
